import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import VoiceRecorderButton from "@/components/VoiceRecorderButton";
import { toast } from "sonner";
import { ChevronLeft, Sparkles, Loader2 } from "lucide-react";

const TOTAL = 9;
const SCALE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const EMPTY = { q1_introspection: "", q2_specificity: "", q3_measurability_baseline: null, q4_attractiveness: "", q5_relevance: "", q6_timeframe: "", q7_commitment: "" };

function VoiceRow({ onText, lang }) {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-2">
      <VoiceRecorderButton compact onTranscribed={(txt) => onText(txt)} lang={lang} />
      <span className="text-xs text-muted-foreground">{t("goal_voice_hint")}</span>
    </div>
  );
}

export default function GoalWizard() {
  const { t, lang } = useI18n();
  usePageMeta(t("goals_section_title"));
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState("");
  const [answers, setAnswers] = useState({ ...EMPTY });
  const [wevaEnabled, setWevaEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  const setA = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const canAdvance = useMemo(() => {
    switch (step) {
      case 0: return title.trim().length > 0;
      case 1: return answers.q1_introspection.trim().length > 0;
      case 2: return answers.q2_specificity.trim().length > 0;
      case 3: return typeof answers.q3_measurability_baseline === "number";
      case 4: return answers.q4_attractiveness.trim().length > 0;
      case 5: return answers.q5_relevance.trim().length > 0;
      case 6: return answers.q6_timeframe.trim().length > 0;
      case 7: return answers.q7_commitment.trim().length > 0;
      default: return true;
    }
  }, [step, title, answers]);

  const stepMeta = {
    0: ["goals_wizard_title_num", "goals_wizard_title_h", "goals_wizard_title_hint"],
    1: ["goals_q1_num", "goals_q1_title", "goals_q1_hint"],
    2: ["goals_q2_num", "goals_q2_title", "goals_q2_hint"],
    3: ["goals_q3_num", "goals_q3_title", "goals_q3_hint"],
    4: ["goals_q4_num", "goals_q4_title", "goals_q4_hint"],
    5: ["goals_q5_num", "goals_q5_title", "goals_q5_hint"],
    6: ["goals_q6_num", "goals_q6_title", "goals_q6_hint"],
    7: ["goals_q7_num", "goals_q7_title", "goals_q7_hint"],
    8: ["goals_wizard_final_num", "goals_wizard_final_h", "goals_wizard_final_hint"],
  }[step];

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/goals", { title: title.trim(), answers, weva_analysis_enabled: wevaEnabled });
      toast.success(t("goals_wizard_save"));
      navigate("/app/goals", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" && d?.hint ? d.hint : apiError(e));
    } finally { setBusy(false); }
  };

  const next = () => { if (step < TOTAL - 1) setStep((n) => n + 1); else save(); };
  const back = () => { if (step === 0) navigate("/app/goals"); else setStep((n) => n - 1); };

  const RECAP = [
    ["goals_wizard_recap_title", title],
    ["goals_q1_short", answers.q1_introspection],
    ["goals_q2_short", answers.q2_specificity],
    ["goals_q3_short", answers.q3_measurability_baseline ? `${answers.q3_measurability_baseline} / 10` : ""],
    ["goals_q4_short", answers.q4_attractiveness],
    ["goals_q5_short", answers.q5_relevance],
    ["goals_q6_short", answers.q6_timeframe],
    ["goals_q7_short", answers.q7_commitment],
  ];

  return (
    <div className="mx-auto max-w-xl space-y-5" data-testid="goal-wizard-screen">
      <div className="flex items-center gap-3">
        <button onClick={back} data-testid="goal-wizard-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 text-foreground">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <p className="mb-1 text-xs font-medium text-muted-foreground">{t("goals_wizard_step_of", { n: step + 1, total: TOTAL })}</p>
          <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${((step + 1) / TOTAL) * 100}%` }} />
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">{t(stepMeta[0])}</p>
        <h1 className="mt-1 font-display text-2xl leading-tight text-foreground">{t(stepMeta[1])}</h1>
        {stepMeta[2] ? <p className="mt-2 text-sm italic text-muted-foreground">{t(stepMeta[2])}</p> : null}
      </div>

      <div className="space-y-3">
        {step === 0 ? (
          <>
            <VoiceRow onText={(txt) => setTitle((p) => (p ? p + " " : "") + txt)} lang={lang} />
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("goals_title_placeholder")} maxLength={200} data-testid="goal-title-input" />
          </>
        ) : null}
        {[1, 2, 4, 5, 7].includes(step) ? (
          <>
            <VoiceRow onText={(txt) => { const key = { 1: "q1_introspection", 2: "q2_specificity", 4: "q4_attractiveness", 5: "q5_relevance", 7: "q7_commitment" }[step]; setA(key, (answers[key] ? answers[key] + " " : "") + txt); }} lang={lang} />
            <Textarea
              value={{ 1: answers.q1_introspection, 2: answers.q2_specificity, 4: answers.q4_attractiveness, 5: answers.q5_relevance, 7: answers.q7_commitment }[step]}
              onChange={(e) => setA({ 1: "q1_introspection", 2: "q2_specificity", 4: "q4_attractiveness", 5: "q5_relevance", 7: "q7_commitment" }[step], e.target.value)}
              placeholder={t({ 1: "goals_q1_placeholder", 2: "goals_q2_placeholder", 4: "goals_q4_placeholder", 5: "goals_q5_placeholder", 7: "goals_q7_placeholder" }[step])}
              className="min-h-[130px]" maxLength={1500} data-testid={`goal-q${step}`}
            />
          </>
        ) : null}
        {step === 3 ? (
          <div>
            <p className="mb-2 text-sm text-muted-foreground">{t("goals_q3_scale_hint")}</p>
            <div className="flex flex-wrap gap-2">
              {SCALE.map((n) => (
                <button key={n} onClick={() => setA("q3_measurability_baseline", n)} data-testid={`goal-scale-${n}`}
                  className={`h-11 w-11 rounded-full border text-sm font-semibold transition-colors ${answers.q3_measurability_baseline === n ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground"}`}>{n}</button>
              ))}
            </div>
            <div className="mt-2 flex justify-between text-[11px] text-muted-foreground"><span>{t("goals_q3_low")}</span><span>{t("goals_q3_high")}</span></div>
          </div>
        ) : null}
        {step === 6 ? (
          <Input value={answers.q6_timeframe} onChange={(e) => setA("q6_timeframe", e.target.value)} placeholder={t("goals_q6_placeholder")} maxLength={500} data-testid="goal-q6" />
        ) : null}
        {step === 8 ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4">
              <div className="min-w-0 flex-1">
                <p className="inline-flex items-center gap-1.5 font-display text-sm text-foreground"><Sparkles className="h-3.5 w-3.5 text-primary" /> {t("goals_weva_enable_title")}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t("goals_weva_enable_body")}</p>
              </div>
              <Switch checked={wevaEnabled} onCheckedChange={setWevaEnabled} data-testid="goal-weva-toggle" />
            </div>
            <p className="font-display text-base text-foreground">{t("goals_wizard_recap")}</p>
            <div className="rounded-2xl border border-border bg-card">
              {RECAP.map(([k, v], i) => (
                <div key={k} className={`px-4 py-2.5 ${i > 0 ? "border-t border-border" : ""}`}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t(k)}</p>
                  <p className="mt-0.5 text-sm text-foreground">{v || "—"}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <Button onClick={next} disabled={!canAdvance || busy} data-testid="goal-wizard-next" className="w-full rounded-full">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (step < TOTAL - 1 ? t("continue") : t("goals_wizard_save"))}
      </Button>
    </div>
  );
}
